/**
 * Shared SED variable UI primitives — used by SEDVariableBrowser and
 * RequirementVariableAccordion (Coverage Matrix in-panel variables).
 */
import React from 'react';
import {
  FileText,
  Sparkles,
  FileBarChart,
  ExternalLink,
  AlertTriangle,
} from 'lucide-react';
import type { SEDVariable } from '../../types/sedAmendments';
import type { SiteDocument } from '../../types/siteRag';
import { sourceLabel, type SourceTone } from '../../lib/sourceLabel';
import {
  defaultReasonFor,
  ago,
  isAttemptedEmpty,
} from '../../lib/providerEvidenceHonesty';
import { getSEDVariableWarnings } from '../../lib/sedVariableSanity';
import { cleanDocFileName } from '../../lib/docFileName';

export const SOURCE_TONE_CLASSES: Record<SourceTone, string> = {
  doc: 'bg-success/15 text-success border-success/30',
  authored: 'bg-primary/15 text-primary border-primary/30',
  research: 'bg-primary/10 text-primary border-primary/30',
  dam: 'bg-info/15 text-info border-info/30',
  provider: 'bg-success/15 text-success border-success/30',
  missing: 'bg-danger/15 text-danger border-danger/30',
};

const EXCERPT_MAX_CHARS = 200;

export function truncateExcerpt(text: string, max: number = EXCERPT_MAX_CHARS): string {
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
}

export function humanizeProvider(id: string): string {
  switch (id) {
    case 'google-maps-elevation':
      return 'Google Maps Elevation API';
    case 'usgs-epqs':
      return 'USGS EPQS';
    case 'site-profile':
      return 'Site Profile';
    default:
      return id;
  }
}

export function formatAttemptStages(
  stages: SEDVariable['attemptStages'],
): string | null {
  if (!stages || stages.length === 0) return null;
  const parts = stages.map(s => `${s.stage} ${s.pointsReturned}/${s.pointsRequested} returned`);
  const last = stages[stages.length - 1];
  const stoppedEarly = last.pointsReturned > 0 && stages.length < 4;
  if (stoppedEarly) parts.push('stopped (sufficient samples)');
  return `Tried ${stages.length} strateg${stages.length === 1 ? 'y' : 'ies'}: ${parts.join(' · ')}`;
}

export function formatActorLabel(actor: { uid: string; email?: string } | string | undefined): string {
  if (!actor) return 'Site Context';
  if (typeof actor === 'string') return actor;
  return actor.email || actor.uid;
}

export function formatVariableDisplayValue(v: SEDVariable): string {
  if (typeof v.value === 'boolean') return v.value ? 'Yes' : 'No';
  if (Array.isArray(v.value)) return v.value.join(', ');
  if (v.value == null || v.value === '') return '—';
  return String(v.value);
}

export function useDocLookup(documents?: SiteDocument[]) {
  const docById = React.useMemo(() => {
    const m = new Map<string, SiteDocument>();
    for (const d of documents ?? []) m.set(d.id, d);
    return m;
  }, [documents]);

  const resolveDocName = React.useCallback(
    (docId: string) => {
      const d = docById.get(docId);
      return d ? cleanDocFileName(d.fileName) : docId;
    },
    [docById],
  );

  return { docById, resolveDocName };
}

interface VariableProvenanceStripProps {
  variable: SEDVariable;
  resolveDocName: (docId: string) => string;
  openDocViewerAt: (docId: string, pageNumber?: number) => void;
}

export const VariableProvenanceStrip: React.FC<VariableProvenanceStripProps> = ({
  variable,
  resolveDocName,
  openDocViewerAt,
}) => {
  const refs = variable.linkedDocumentRefs ?? [];
  const primaryRef = refs[0];
  const supportingRefs = refs.slice(1);
  const claim = variable.authoredClaims?.[0];
  const report = variable.derivedFromReport;

  const shouldRenderDoc = variable.status === 'verified-from-doc' && !!primaryRef;
  const shouldRenderContext = variable.status === 'verified-from-context' && !!claim && !!claim.sourceSpan;
  const shouldRenderReport = variable.status === 'verified-from-report' && !!report;

  if (!shouldRenderDoc && !shouldRenderContext && !shouldRenderReport) return null;

  return (
    <div
      data-testid={`variable-lineage-${variable.path}`}
      className="mt-1 type-body text-text-secondary"
    >
      {shouldRenderDoc && primaryRef && (
        <>
          <div className="flex items-center gap-1.5 flex-wrap">
            <FileText className="w-3 h-3 shrink-0 text-text-muted" aria-hidden="true" />
            <span className="font-medium text-text-primary">{resolveDocName(primaryRef.docId)}</span>
            {primaryRef.pageNumber != null && (
              <span className="text-text-muted">· page {primaryRef.pageNumber}</span>
            )}
            {primaryRef.sectionHeading && (
              <span className="text-text-muted" title={primaryRef.sectionHeading}>
                · §{truncateExcerpt(primaryRef.sectionHeading, 60)}
              </span>
            )}
            <button
              type="button"
              data-testid={`open-source-${variable.path}`}
              onClick={() => openDocViewerAt(primaryRef.docId, primaryRef.pageNumber)}
              className="ml-1 underline text-primary hover:text-primary/80 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
              aria-label={`Open ${resolveDocName(primaryRef.docId)} at ${
                primaryRef.pageNumber != null ? `page ${primaryRef.pageNumber}` : 'the source page'
              }`}
            >
              Open at page
            </button>
          </div>
          {primaryRef.excerptPreview && (
            <div
              className="mt-0.5 ml-4 italic text-text-muted border-l-2 border-border pl-2"
              title={primaryRef.excerptPreview}
            >
              "{truncateExcerpt(primaryRef.excerptPreview)}"
            </div>
          )}
          {supportingRefs.length > 0 && (
            <details className="ml-4 mt-0.5">
              <summary
                className="cursor-pointer text-text-muted hover:text-text-primary"
                data-testid={`variable-lineage-${variable.path}-more-summary`}
              >
                {supportingRefs.length} more supporting ref{supportingRefs.length === 1 ? '' : 's'}
              </summary>
              <div className="mt-1 space-y-1">
                {supportingRefs.map((r, i) => (
                  <div
                    key={`${r.docId}-${i}`}
                    className="flex items-center gap-1.5 flex-wrap"
                    data-testid={`variable-lineage-${variable.path}-supporting-${i}`}
                  >
                    <FileText className="w-3 h-3 shrink-0 text-text-muted" aria-hidden="true" />
                    <span>{resolveDocName(r.docId)}</span>
                    {r.pageNumber != null && (
                      <span className="text-text-muted">· page {r.pageNumber}</span>
                    )}
                    <button
                      type="button"
                      onClick={() => openDocViewerAt(r.docId, r.pageNumber)}
                      className="ml-1 underline text-primary hover:text-primary/80 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
                      aria-label={`Open ${resolveDocName(r.docId)} at ${
                        r.pageNumber != null ? `page ${r.pageNumber}` : 'the source page'
                      }`}
                    >
                      Open
                    </button>
                  </div>
                ))}
              </div>
            </details>
          )}
        </>
      )}

      {shouldRenderContext && claim && (
        <div className="flex items-start gap-1.5">
          <Sparkles className="w-3 h-3 shrink-0 mt-0.5 text-primary" aria-hidden="true" />
          <div className="min-w-0">
            <div className="text-text-primary">
              <span className="font-medium">Authored</span>
              {' '}by {formatActorLabel(claim.writtenBy)}
              {claim.lastWrittenAt && (
                <span className="text-text-muted"> · {claim.lastWrittenAt.slice(0, 10)}</span>
              )}
            </div>
            <div
              className="italic text-text-muted border-l-2 border-border pl-2 mt-0.5"
              title={claim.sourceSpan}
            >
              "{truncateExcerpt(claim.sourceSpan)}"
            </div>
          </div>
        </div>
      )}

      {shouldRenderReport && report && (
        <div className="flex items-center gap-1.5 flex-wrap">
          <FileBarChart className="w-3 h-3 shrink-0 text-info" aria-hidden="true" />
          <span className="font-medium text-text-primary">
            {report.reportType} v{report.reportVersion}
          </span>
          {Number.isFinite(report.generatedAt) && report.generatedAt > 0 && (
            <span className="text-text-muted">
              · {new Date(report.generatedAt).toISOString().slice(0, 10)}
            </span>
          )}
          {report.sectionId && (
            <span className="text-text-muted">· §{report.sectionId}</span>
          )}
          {report.verdict && (
            <span className="text-text-muted" title={report.verdict}>
              · {truncateExcerpt(report.verdict, 80)}
            </span>
          )}
          <ExternalLink className="w-2.5 h-2.5 shrink-0 text-text-muted opacity-50" aria-hidden="true" />
        </div>
      )}
    </div>
  );
};

interface SEDVariableRowProps {
  variable: SEDVariable;
  resolveDocName: (docId: string) => string;
  openDocViewerAt: (docId: string, pageNumber?: number) => void;
  isFocused?: boolean;
  rowRef?: React.Ref<HTMLDivElement>;
  onToggleLineage?: () => void;
  lineageExpanded?: boolean;
  showLineageToggle?: boolean;
  showSourceBadge?: boolean;
  simpleSourceAction?: boolean;
  compact?: boolean;
}

/** Read-only variable row with source chip + provenance strip. */
export const SEDVariableRow: React.FC<SEDVariableRowProps> = ({
  variable: v,
  resolveDocName,
  openDocViewerAt,
  isFocused = false,
  rowRef,
  onToggleLineage,
  lineageExpanded = false,
  showLineageToggle = true,
  showSourceBadge = true,
  simpleSourceAction = false,
  compact = false,
}) => {
  const { label, tone } = sourceLabel(v);

  return (
    <div
      ref={rowRef}
      data-testid={`sed-variable-row-${v.path}`}
      className={`${compact ? 'py-1.5' : 'py-2'} ${isFocused ? 'ring-2 ring-warning ring-offset-2 rounded-sm' : ''}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-medium text-text-muted">{v.label}</span>
            {v.isUserAmended && (
              <span
                className="type-meta font-bold uppercase tracking-widest px-1 py-0.5 bg-primary/10 text-primary rounded-md border border-primary/30"
                data-testid={`sed-variable-amended-badge-${v.path}`}
              >
                Amended
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className="text-[11px] font-mono font-bold text-foreground">
              {formatVariableDisplayValue(v)}
            </span>
            {v.unit && <span className="type-body text-text-secondary">{v.unit}</span>}
            {getSEDVariableWarnings(v).map((w, i) => (
              <span
                key={`${v.path}-warn-${i}`}
                className={`type-meta px-1 py-0.5 border ${
                  w.severity === 'error'
                    ? 'border-danger/50 bg-danger/10 text-danger'
                    : 'border-warning/50 bg-warning/10 text-warning'
                }`}
                data-testid={`sed-variable-sanity-${v.path}`}
                title={w.message}
              >
                {w.message}
              </span>
            ))}
            {showSourceBadge && <span
              data-testid={`source-chip-${v.path}`}
              data-tone={tone}
              className={`inline-flex items-center px-1.5 py-0.5 type-meta uppercase tracking-widest border rounded ${SOURCE_TONE_CLASSES[tone]}`}
              title={label}
              aria-label={`Source: ${label}`}
            >
              {label}
            </span>}
          </div>
          {isAttemptedEmpty(v.value, v.attemptedProvider) && (() => {
            const reason = v.emptyReason ?? defaultReasonFor(v.attemptResult);
            const attemptedAgoText = ago(v.lastAttemptedAt);
            const stagesTooltip = formatAttemptStages(v.attemptStages);
            const baseTooltip = attemptedAgoText
              ? `${reason}. Last attempted ${attemptedAgoText}.`
              : reason;
            const tooltip = stagesTooltip ? `${stagesTooltip}. ${baseTooltip}` : baseTooltip;
            return (
              <div
                className="flex items-center gap-1 mt-1 type-body text-warning"
                data-testid={`sed-variable-attempted-empty-${v.path}`}
                title={tooltip}
              >
                <AlertTriangle className="w-2.5 h-2.5 shrink-0" aria-hidden="true" />
                <span className="font-bold">{humanizeProvider(v.attemptedProvider!)}</span>
                <span>— {reason}</span>
              </div>
            );
          })()}
          <VariableProvenanceStrip
            variable={v}
            resolveDocName={resolveDocName}
            openDocViewerAt={openDocViewerAt}
          />
        </div>
        {showLineageToggle && onToggleLineage && (
          <button
            type="button"
            onClick={onToggleLineage}
            className={simpleSourceAction ? 'shrink-0 text-[10px] text-text-muted hover:text-primary focus-visible:outline focus-visible:outline-primary' : `shrink-0 type-meta px-1.5 py-0.5 border transition-colors ${
              lineageExpanded
                ? 'border-primary text-primary bg-primary/5'
                : 'border-border text-text-secondary hover:text-primary hover:border-primary'
            }`}
            data-testid={`coverage-panel-variable-lineage-${v.path}`}
            aria-expanded={lineageExpanded}
          >
            {simpleSourceAction ? 'Source details' : 'Lineage'}
          </button>
        )}
      </div>
    </div>
  );
};
